import { Bundle } from '../Models/Bundle';
import { IComponentModel } from '../Models/IComponentModel';
import { IContainerComponentModel } from '../Models/IContainerComponentModel';
import { DesignerMessage, MESSAGE_TYPES, SHARED_COMPONENT_ROOT_TYPE } from '../Types/DesignerMessages';
import { ComponentPath, getComponentAtPath, resolveSharedComponentSelection } from './ComponentTreeUtils';
import { getComponentTreePath } from './VirtualComponentCollectionUtils';

type SharedComponentSelection = {
	sharedComponentId: string;
	referenceComponentPath: ComponentPath;
};

type BuildBundleComponentUpdatedMessageArgs = {
	previousBundle?: Bundle;
	nextBundle: Bundle;
	currentView: IContainerComponentModel;
	selectedPath: ComponentPath | null;
	selectedSharedComponentContext?: SharedComponentSelection | null;
};

function getComponentFromRoot(root: IContainerComponentModel, path: ComponentPath): IComponentModel | null {
	if (path.length === 1 && path[0] === root.Id) {
		return root;
	}

	const relativePath = path[0] === root.Id ? path.slice(1) : path;
	return getComponentAtPath(root.Children ?? [], relativePath);
}

function getParentPath(path: ComponentPath): ComponentPath {
	return path.length === 0 ? [] : path.slice(0, -1);
}

function getParentComponentFromView(view: IContainerComponentModel, parentPath: ComponentPath): IComponentModel | null {
	return parentPath.length === 0 ? view : getComponentAtPath(view.Children ?? [], parentPath);
}

function childrenChanged(previousComponent: IComponentModel | null, nextComponent: IComponentModel | null): boolean {
	const previousChildren = (previousComponent as IContainerComponentModel | null)?.Children;
	const nextChildren = (nextComponent as IContainerComponentModel | null)?.Children;
	if (!Array.isArray(previousChildren) || !Array.isArray(nextChildren)) {
		return false;
	}

	if (previousChildren.length !== nextChildren.length) {
		return true;
	}

	return previousChildren.some((child, index) => child.Id !== nextChildren[index]?.Id);
}

function toComponentUpdatedMessage(
	scope: 'View' | 'SharedComponent',
	ownerId: string,
	componentPath: ComponentPath,
	previousComponent: IComponentModel | null,
	nextComponent: IComponentModel | null,
): DesignerMessage | null {
	if (!previousComponent || !nextComponent || previousComponent.Id !== nextComponent.Id) {
		return null;
	}

	return {
		type: MESSAGE_TYPES.BUNDLE_COMPONENT_UPDATED,
		payload: { scope, ownerId, componentPath, component: nextComponent },
	};
}

function toParentUpdatedMessageForChildrenChange(
	scope: 'View' | 'SharedComponent',
	ownerId: string,
	componentPath: ComponentPath,
	getPreviousParentComponent: (parentPath: ComponentPath) => IComponentModel | null,
	getNextParentComponent: (parentPath: ComponentPath) => IComponentModel | null,
): DesignerMessage | null {
	const parentPath = getParentPath(componentPath);
	const previousParent = getPreviousParentComponent(parentPath);
	const nextParent = getNextParentComponent(parentPath);

	if (!childrenChanged(previousParent, nextParent)) {
		return null;
	}

	return toComponentUpdatedMessage(scope, ownerId, parentPath, previousParent, nextParent);
}

export function buildBundleComponentUpdatedMessage({
	previousBundle,
	nextBundle,
	currentView,
	selectedPath,
	selectedSharedComponentContext,
}: BuildBundleComponentUpdatedMessageArgs): DesignerMessage | null {
	const componentTreePath = getComponentTreePath(selectedPath);
	if (!previousBundle || !componentTreePath || componentTreePath.length === 0) {
		return null;
	}

	if (currentView.Type === SHARED_COMPONENT_ROOT_TYPE) {
		const previousSharedComponent = previousBundle.SharedComponents.find((c) => c.Id === currentView.Id);
		const nextSharedComponent = nextBundle.SharedComponents.find((c) => c.Id === currentView.Id);
		if (!previousSharedComponent || !nextSharedComponent) {
			return null;
		}

		const parentUpdate = toParentUpdatedMessageForChildrenChange(
			'SharedComponent',
			nextSharedComponent.Id,
			componentTreePath,
			(parentPath) => getComponentFromRoot(previousSharedComponent.Component as IContainerComponentModel, parentPath),
			(parentPath) => getComponentFromRoot(nextSharedComponent.Component as IContainerComponentModel, parentPath),
		);
		if (parentUpdate) {
			return parentUpdate;
		}

		return toComponentUpdatedMessage(
			'SharedComponent',
			nextSharedComponent.Id,
			componentTreePath,
			getComponentFromRoot(previousSharedComponent.Component as IContainerComponentModel, componentTreePath),
			getComponentFromRoot(nextSharedComponent.Component as IContainerComponentModel, componentTreePath),
		);
	}

	if (selectedSharedComponentContext) {
		const nextSelection = resolveSharedComponentSelection(nextBundle, componentTreePath, selectedSharedComponentContext);
		if (!nextSelection) {
			return null;
		}

		const previousSharedComponent = previousBundle.SharedComponents.find((c) => c.Id === nextSelection.sharedComponent.Id);
		if (!previousSharedComponent) {
			return null;
		}

		const parentUpdate = toParentUpdatedMessageForChildrenChange(
			'SharedComponent',
			nextSelection.sharedComponent.Id,
			nextSelection.pathInSharedComponent,
			(parentPath) => getComponentFromRoot(previousSharedComponent.Component as IContainerComponentModel, parentPath),
			(parentPath) => getComponentFromRoot(nextSelection.root, parentPath),
		);
		if (parentUpdate) {
			return parentUpdate;
		}

		return toComponentUpdatedMessage(
			'SharedComponent',
			nextSelection.sharedComponent.Id,
			nextSelection.pathInSharedComponent,
			getComponentFromRoot(previousSharedComponent.Component as IContainerComponentModel, nextSelection.pathInSharedComponent),
			getComponentFromRoot(nextSelection.root, nextSelection.pathInSharedComponent),
		);
	}

	const previousView = previousBundle.Views.find((v) => v.Id === currentView.Id);
	const nextView = nextBundle.Views.find((v) => v.Id === currentView.Id);
	if (!previousView || !nextView) {
		return null;
	}

	const parentUpdate = toParentUpdatedMessageForChildrenChange(
		'View',
		nextView.Id,
		componentTreePath,
		(parentPath) => getParentComponentFromView(previousView, parentPath),
		(parentPath) => getParentComponentFromView(nextView, parentPath),
	);
	if (parentUpdate) {
		return parentUpdate;
	}

	return toComponentUpdatedMessage(
		'View',
		nextView.Id,
		componentTreePath,
		getComponentAtPath(previousView.Children ?? [], componentTreePath),
		getComponentAtPath(nextView.Children ?? [], componentTreePath),
	);
}

export function buildBundleMetadataUpdatedMessage(previousBundle: Bundle | undefined, nextBundle: Bundle): DesignerMessage | null {
	if (!previousBundle) {
		return null;
	}

	const viewsChanged = previousBundle.Views !== nextBundle.Views;
	const sharedComponentsChanged = previousBundle.SharedComponents !== nextBundle.SharedComponents;
	const reactBundleChanged = previousBundle.ReactBundle !== nextBundle.ReactBundle;
	if (viewsChanged || sharedComponentsChanged || reactBundleChanged) {
		return null;
	}

	const payload: Extract<DesignerMessage, { type: typeof MESSAGE_TYPES.BUNDLE_METADATA_UPDATED }>['payload'] = {};

	if (previousBundle.Themes !== nextBundle.Themes) {
		payload.themes = nextBundle.Themes;
	}

	if (previousBundle.Settings !== nextBundle.Settings) {
		payload.settings = nextBundle.Settings;
	}

	if (previousBundle.EmbeddedResources !== nextBundle.EmbeddedResources) {
		payload.embeddedResources = nextBundle.EmbeddedResources;
	}

	return Object.keys(payload).length > 0
		? {
				type: MESSAGE_TYPES.BUNDLE_METADATA_UPDATED,
				payload,
			}
		: null;
}

export function buildReactBundleUpdatedMessage(previousBundle: Bundle | undefined, nextBundle: Bundle): DesignerMessage | null {
	if (!previousBundle || previousBundle.ReactBundle === nextBundle.ReactBundle || !nextBundle.ReactBundle) {
		return null;
	}

	if (
		previousBundle.Views !== nextBundle.Views ||
		previousBundle.SharedComponents !== nextBundle.SharedComponents ||
		previousBundle.Themes !== nextBundle.Themes ||
		previousBundle.Settings !== nextBundle.Settings ||
		previousBundle.EmbeddedResources !== nextBundle.EmbeddedResources
	) {
		return null;
	}

	return {
		type: MESSAGE_TYPES.REACT_BUILD_UPDATED,
		payload: { buildId: nextBundle.ReactBundle.BuildId, reactBundle: nextBundle.ReactBundle },
	};
}
